import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { ProductsManagementRepository } from '@app/data-layer/repositories';
import { ProductManagementModel } from '@app/data-layer/models/management/products';
import { IDefaultSelect } from '@interfaces/default-select';
import { TranslateService } from '@ngx-translate/core';
import { enumBillItemProductCode } from '@app/data-layer/enums/payments';
import { EnumHelper } from '@app/shared/helpers/enum.helper';

@Injectable({ providedIn: 'root' })
export class ProductOptionsService {
  private cache$: Observable<IDefaultSelect[]> | null = null;

  constructor(private productsRepo: ProductsManagementRepository, private translateService: TranslateService) {}

  public getProductOptions(forceRefresh: boolean = false): Observable<IDefaultSelect[]> {
    if (!forceRefresh && this.cache$) {
      return this.cache$;
    }

    this.cache$ = this.fetchAllProducts().pipe(
      map((products) => products.map((p) => this.mapToSelect(p))),
      map((items) => this.sortByLabel(items)),
      shareReplay(1),
    );

    return this.cache$;
  }

  public invalidateCache(): void {
    this.cache$ = null;
  }

  private fetchAllProducts(): Observable<ProductManagementModel[]> {
    // Existing repo uses paginated search; per requirement, call without offset/limit to get all
    return this.productsRepo.search({}).pipe(map((res) => res.items || []));
  }

  private mapToSelect(product: ProductManagementModel): IDefaultSelect {
    const enumKey = this.getEnumKeyFromProductCode(product?.code);
    const i18nKey = enumKey ? `PRODUCT_CODE.${enumKey}` : '';
    const translated = i18nKey ? this.translateService.instant(i18nKey) : '';
    const label = translated && translated !== i18nKey ? translated : product?.name || this.prettifyEnumKey(enumKey);

    return {
      id: product?.id,
      value: product?.code,
      label: label,
    };
  }

  private getEnumKeyFromProductCode(code: string | number | null | undefined): string | null {
    if (code == null) {
      return null;
    }
    // Values of enum are the product codes (e.g., 'ISF', 'BRE', ...). We want the key (e.g., 'ISF', 'BROKER_ENTRY').
    const key = EnumHelper.getEnumKeyByValue(enumBillItemProductCode as any, String(code));
    return key || null;
  }

  private prettifyEnumKey(key: string | null): string {
    if (!key) {
      return '';
    }
    return key
      .toLowerCase()
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  private sortByLabel(items: IDefaultSelect[]): IDefaultSelect[] {
    return [...items].sort((a, b) => a.label.localeCompare(b.label));
  }
}
